// Copyright 2020 Sorama B.V.

import { autoinject, Disposable, observable } from "aurelia-framework";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { IComposableCanvasControls } from "controls/composable-canvas/ComposableCanvas";
import { IComposableCanvasController } from "controls/composable-canvas/ComposableCanvasController";
import { LinkedMediaObjectDTO, TimelineEntryDTO } from "datatypes/SoramaWebApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    WorkflowPropertyBindingMode,
    WorkflowPropertyScope,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { IProjectManagementService } from "services/Project/IProjectManagementService";

@autoinject
export class CanvasAdd implements IWorkflowModuleLifetime {
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    protected selectedTab: "image" | "video" | "timeline" | "file" = "image";

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, scope: WorkflowPropertyScope.workflow })
    public canvasId: string;

    private canvasControllerDisposable: Disposable;

    public canvasControls: IComposableCanvasControls;

    @observable
    protected selectedMediaObject: LinkedMediaObjectDTO;

    constructor(
        private readonly canvasController: IComposableCanvasController,
        protected readonly projectManagementService: IProjectManagementService,
        private readonly shellviewManager: ShellViewManager
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void | Promise<void> {
        this.canvasIdChanged(this.canvasId);
    }

    public deactivateModule(): void | Promise<void> {
        if (this.canvasControllerDisposable) {
            this.canvasControllerDisposable.dispose();
        }
    }

    protected canvasIdChanged(newValue: string) {
        if (this.canvasControllerDisposable) {
            this.canvasControllerDisposable.dispose();
            delete this.canvasControllerDisposable;
        }

        if (newValue) {
            this.canvasControllerDisposable = this.canvasController.registerCanvasController(
                newValue,
                this,
                "canvasControls"
            );
        }
    }

    protected addTimelineEntryToCanvas = (entry: TimelineEntryDTO) => {
        if (!entry.snapshot) {
            // Cannot add this
            console.warn("Cannot add timeline entry to canvas as it does not contain a snapshot");
            return;
        }

        if (!this.canvasControls) {
            return;
        }

        this.canvasControls.addMediaObject(entry.snapshot.id);
    };

    protected selectedMediaObjectChanged(): void {
        if (!this.canvasControls || this.selectedMediaObject == undefined) {
            return;
        }

        this.canvasControls.addMediaObject(this.selectedMediaObject.id);
    }

    protected editProject(): void {
        this.shellviewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.ProjectManagementModule);
    }
}
